import { useCallback } from 'react';
import { setTaskCheck } from '@apis/schedule';
import { ChecklistTickRefusedError } from '@apis/checklists';
import { useRestoreServerState } from './useRestoreServerState';
import type { IBlackboardEntry, ITaskAttachment, SiteId, TaskId } from '@interfaces/IScheduler';
import { useImmediateWritePersist } from './useImmediateWritePersist';

/** The client-supplied evidence for a checklist completion. */
export interface ICheckEvidence {
  attachedPhoto?: ITaskAttachment;
  comment?: string;
  blackboard?: IBlackboardEntry;
}

interface ICheckWrite {
  completed: boolean;
  evidence?: ICheckEvidence;
}

/**
 * Immediate-write persistence for a task's checklist COMPLETION (phase 2 — ticks
 * leave the Save path; only DEFINITIONS stay draftable in the Edit tab). Returns
 * `persist(itemId, completed, evidence?)`, which PUTs the tick through the
 * granular `checks/:itemId` endpoint on the shared `immediateWriteChannel`. One
 * channel per (task, checklist ITEM): rapid on/off toggles of the same item
 * coalesce to the final state, and two different items are independent
 * serialized writes that can't erase each other. A tick given up is rolled back
 * and a toast says so. No-op when `scheduleNumber` is absent
 * (Router-free tests) — the store still updates optimistically. Pending writes
 * flush on unmount / task switch.
 *
 * A tick the server refuses for good (the item was amended away, evidence is
 * missing, the promise was taken back) is an answer, not a failure: nothing
 * retries. The plan and the ticks are re-read from the server — which undoes
 * the optimistic tick — and `onRefused` says so.
 */
export function useTaskChecklistPersist(
  siteId: SiteId,
  scheduleNumber: string | undefined,
  taskId: TaskId,
  onRefused?: () => void,
): (itemId: string, completed: boolean, evidence?: ICheckEvidence) => void {
  const { write } = useImmediateWritePersist(siteId, scheduleNumber, taskId);
  const restore = useRestoreServerState(siteId);

  return useCallback(
    (itemId: string, completed: boolean, evidence?: ICheckEvidence) => {
      write<ICheckWrite>(['check', itemId], { completed, evidence }, (v, ctx) =>
        setTaskCheck(ctx.siteId, ctx.scheduleNumber, ctx.taskId, itemId, v).catch(async (err: unknown) => {
          if (!(err instanceof ChecklistTickRefusedError)) throw err;
          await restore(ctx.scheduleNumber);
          onRefused?.();
        }),
      );
    },
    [write, restore, onRefused],
  );
}
